import { GenericTable, Spinner } from "@visiblethread/react-components";
import ScrollerPanel from "components/ScrollerPanel";
import { idPropType, VIRTUALIZE_TABLE } from "constants/app";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { forwardRef, useEffect } from "react";
import isNullish from "utils/isNullish";

import columns from "./columns";
import styles from "./CoverageRequirementsTable.module.scss";

const CoverageRequirementsTable = forwardRef(
  (
    {
      rows,
      emptyMessage = "No requirements linked to this section.",
      isLoading = false,
      onSelectRow,
      selectedRowIndex = -1,
      scrollToRowId = null,
      showAddressedIn = true,
    },
    tableRef
  ) => {
    useEffect(() => {
      if (isNullish(scrollToRowId)) {
        return;
      }
      const index = rows.findIndex((row) => row.id === scrollToRowId);
      if (index >= 0) {
        // Both options stated rather than left to the library's default: the eased scroll is the
        // point of stepping, and passing a partial options object would replace the default whole.
        tableRef?.current?.scrollToRow(index, {
          align: "center",
          behavior: "smooth",
        });
      }
    }, [scrollToRowId, rows]);

    if (isLoading) {
      return (
        <div
          className={styles.empty}
          data-testid="coverage-requirements-loading"
        >
          <Spinner />
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div className={styles.empty} data-testid="coverage-requirements-empty">
          {emptyMessage}
        </div>
      );
    }

    return (
      <ScrollerPanel className={styles.wrapper}>
        <GenericTable
          cellClassName={styles.cell}
          headerCellClassName={styles.headerCell}
          columns={columns({ showAddressedIn })}
          data={rows}
          defaultSortColumnIndex={-1}
          enableRowSelection
          isVirtualized={VIRTUALIZE_TABLE}
          onRowClick={onSelectRow}
          ref={tableRef}
          selectedIndex={selectedRowIndex}
        />
      </ScrollerPanel>
    );
  }
);

CoverageRequirementsTable.displayName = "CoverageRequirementsTable";

CoverageRequirementsTable.propTypes = {
  emptyMessage: string,
  isLoading: bool,
  onSelectRow: func.isRequired,
  rows: arrayOf(
    shape({
      addressedIn: arrayOf(string),
      categories: arrayOf(string),
      id: idPropType,
      isGap: bool,
      page: number,
      pointIds: arrayOf(string),
      refs: arrayOf(string),
      restatement: string,
      text: string,
    })
  ).isRequired,
  scrollToRowId: idPropType,
  selectedRowIndex: number,
  showAddressedIn: bool,
};

export default CoverageRequirementsTable;
